var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) =>
  key in obj
    ? __defProp(obj, key, {
        enumerable: true,
        configurable: true,
        writable: true,
        value,
      })
    : (obj[key] = value);
var __publicField = (obj, key, value) => {
  __defNormalProp(obj, typeof key !== 'symbol' ? key + '' : key, value);
  return value;
};

// src/message.ts
var MESSAGE = /* @__PURE__ */ ((MESSAGE2) => {
  MESSAGE2['BROADCAST_CLIENTS'] = 'BROADCAST_CLIENTS';
  MESSAGE2['DIRECT_CLIENT'] = 'DIRECT_CLIENT';
  MESSAGE2['CLIENT_SEND'] = 'CLIENT_SEND';
  MESSAGE2['CLIENT_REQUEST'] = 'CLIENT_REQUEST';
  MESSAGE2['SUBSCRIBE'] = 'SUBSCRIBE';
  MESSAGE2['BROADCAST_ALL_SUBS'] = 'BROADCAST_ALL_SUBS';
  MESSAGE2['DIRECT_ANY_SUB'] = 'DIRECT_ANY_SUB';
  MESSAGE2['ANY_SUB_SEND'] = 'ANY_SUB_SEND';
  MESSAGE2['ANY_SUB_REQUEST'] = 'ANY_SUB_REQUEST';
  MESSAGE2['BROADCAST_SUBS'] = 'BROADCAST_SUBS';
  MESSAGE2['DIRECT_SUB'] = 'DIRECT_SUB';
  MESSAGE2['SUB_SEND'] = 'SUB_SEND';
  MESSAGE2['SUB_REQUEST'] = 'SUB_REQUEST';
  MESSAGE2['UNSUBSCRIBE'] = 'UNSUBSCRIBE';
  return MESSAGE2;
})(MESSAGE || {});
function encodeMessage(payload) {
  return [
    payload.namespace,
    payload.subId,
    payload.label,
    payload.data,
    payload.handler,
  ];
}
function decodeMessage(payload) {
  const [n, i, l, d, h] = payload;
  if (n === null) throw new Error('this is an internal message.');
  return {
    namespace: n,
    subId: i ?? null,
    label: l,
    data: d,
    handler: h,
  };
}

// src/service/error.ts
var ServiceDefinitionError = class extends Error {
  name = 'ServiceDefinitionError';
  constructor(namespace, message, label) {
    const prefix =
      !label || label === namespace ? namespace : `${namespace}.${label}`;
    super(`${prefix}: ${message}`);
  }
};

// src/util.ts
var PLACEHOLDER_BUS = new Proxy(
  {},
  {
    get(_target, prop, _receiver) {
      return (...m) => {
        console.debug(`${prop}: vroom vroom`, ...m);
        return m;
      };
    },
  },
);
var SUBSCRIBE_LABEL = /^SUBSCRIBE_([A-Z_]+)$/;
var UNSUBSCRIBE_LABEL = /^UNSUBSCRIBE_([A-Z_]+)$/;
var PING_PONG_INTERVAL_MS = 30 * 1e3;
var PING_TIMEOUT_MS = 5 * 1e3;
function createHardMap(subject = 'Thang', items) {
  const map = new Map(items);
  const get2 = map.get.bind(map);
  map.get = (key) => {
    const value = get2(key);
    if (value === void 0) throw new Error(`${subject} ${key} is not in map!`);
    else return value;
  };
  return map;
}

// src/instance.ts
function instanceProps(base, enumerable) {
  return Object.fromEntries(
    Object.entries(base).map(([prop, value]) => [prop, { value, enumerable }]),
  );
}
function createInstance(ctor, props, privateProps, ...args) {
  const instance = new ctor(instanceProps(props, true), ...args);
  if (ctor.__INSTANCE !== instance.__INSTANCE)
    throw new Error('constructors got all goofed up!');
  validateInstance(props, instance);
  Object.defineProperties(instance, instanceProps(privateProps, false));
  validateInstance(privateProps, instance);
  return instance;
}
function validateInstance(want, got, subject = 'Thang') {
  try {
    if (!got)
      throw new Error(`invalid ${subject}: expected ${want}, got... NOTHING`);
    for (const [k, v] of Object.entries(want))
      if (got[k] !== v)
        throw new Error(`invalid ${subject}: expected ${want}, got ${got}`);
  } catch {
    console.error(
      'one of these did not wish to be in an error message:',
      want,
      got,
    );
    throw new Error(`invalid ${subject}: [blew up when we tried to compare??]`);
  }
}

// src/service/runtime-validation.ts
function validateInstanceCtor(runtime, base) {
  return (
    runtime &&
    typeof runtime === 'function' &&
    '__INSTANCE' in runtime && // Too narrow, hank hill mode
    base.__INSTANCE === runtime.__INSTANCE
  );
}
function hasKey(obj, key) {
  return key in obj;
}
function hasCtor(schema, key, ctor) {
  return (
    schema !== null &&
    typeof schema === 'object' &&
    hasKey(schema, key) &&
    validateInstanceCtor(schema[key], ctor)
  );
}

// src/service/client-instance.ts
function createClientMap() {
  const map = createHardMap('service');
  const set2 = map.set.bind(map);
  map.set = (schema, id, bus) => {
    const namespace = validateNamespace(schema.NAMESPACE, map, true);
    if (map.has(namespace))
      throw new Error(`service ${namespace} already registered!`);
    const subs = createHardMap();
    const props = { id, namespace };
    const privateProps = { subs, ...bus };
    const service = createInstance(schema.Client, props, privateProps);
    const record = { namespace, service, schema, subs };
    set2(namespace, record);
    return record;
  };
  return map;
}
var __CLIENT_SERVICE_INSTANCE = Symbol('ClientServiceInstance');
var __CLIENT_SUB_INSTANCE = Symbol('ClientSubInstance');
var ClientServiceInstance = class {
  __INSTANCE = __CLIENT_SERVICE_INSTANCE;
  id;
  namespace;
  constructor(props) {
    Object.defineProperties(this, props);
  }
};
__publicField(ClientServiceInstance, '__INSTANCE', __CLIENT_SERVICE_INSTANCE);
var ClientSubInstance = class {
  __INSTANCE = __CLIENT_SUB_INSTANCE;
  namespace;
  label;
  id;
  clientId;
  constructor(props) {
    Object.defineProperties(this, props);
  }
};
__publicField(ClientSubInstance, '__INSTANCE', __CLIENT_SUB_INSTANCE);
function validateClientSchema(namespace, subscriptions, messageLabels, schema) {
  if (!hasKey(schema, 'NAMESPACE') || schema.NAMESPACE !== namespace)
    throw new Error('bad namespace');
  if (!hasCtor(schema, 'Client', ClientServiceInstance))
    throw new Error('bad service ctor');
  if (!hasKey(schema, 'SUBSCRIPTION'))
    throw new Error('missing subscription enum');
  if (!hasKey(schema, 'MESSAGE')) throw new Error('missing message label enum');
  if (
    !hasKey(schema, 'subs') ||
    schema.subs === null ||
    typeof schema.subs !== 'object'
  )
    throw new Error('missing submap');
  for (const label of Object.keys(subscriptions)) {
    if (subscriptions[label] !== label)
      throw new Error(`malformed subscriptions label ${label}`);
    if (!hasCtor(schema, label, ClientSubInstance))
      throw new Error(`bad sub ctor for ${label}`);
  }
  for (const label of Object.keys(messageLabels)) {
    if (messageLabels[label] !== label)
      throw new Error(`malformed message label ${label}`);
  }
  return schema;
}

// src/service/worker-instance.ts
function createWorkerMap() {
  const map = createHardMap('service');
  const set2 = map.set.bind(map);
  function gSet(schema, bus, authController) {
    const namespace = validateNamespace(schema.NAMESPACE, map, true);
    if (map.has(namespace))
      throw new Error(`service ${namespace} already registered!`);
    const clients = createHardMap();
    const subs = createHardMap();
    const props = { namespace, authController };
    const privateProps = { clients, subs, ...bus };
    const service = createInstance(schema.Worker, props, privateProps);
    const record = {
      namespace,
      schema,
      service,
      clients,
      subs,
    };
    set2(namespace, record);
    return record;
  }
  return Object.defineProperty(map, 'set', { value: gSet });
}
var __WORKER_SERVICE_INSTANCE = Symbol('WorkerServiceInstance');
var __WORKER_CLIENT_INSTANCE = Symbol('WorkerClientInstance');
var __WORKER_SUB_INSTANCE = Symbol('WorkerSubInstance');
var WorkerServiceInstance = class {
  __INSTANCE = __WORKER_SERVICE_INSTANCE;
  namespace;
  authController;
  constructor(props) {
    Object.defineProperties(this, props);
  }
};
__publicField(WorkerServiceInstance, '__INSTANCE', __WORKER_SERVICE_INSTANCE);
var WorkerClientInstance = class {
  __INSTANCE = __WORKER_CLIENT_INSTANCE;
  namespace;
  id;
  constructor(props) {
    Object.defineProperties(this, props);
  }
};
__publicField(WorkerClientInstance, '__INSTANCE', __WORKER_CLIENT_INSTANCE);
var WorkerSubInstance = class {
  __INSTANCE = __WORKER_SUB_INSTANCE;
  namespace;
  label;
  id;
  clientId;
  constructor(props) {
    Object.defineProperties(this, props);
  }
};
__publicField(WorkerSubInstance, '__INSTANCE', __WORKER_SUB_INSTANCE);
function validateWorkerSchema(namespace, subscriptions, messageLabels, schema) {
  if (!hasKey(schema, 'NAMESPACE') || schema.NAMESPACE !== namespace)
    throw new Error('bad namespace');
  if (!hasCtor(schema, 'Worker', WorkerServiceInstance))
    throw new Error('bad service ctor');
  if (!hasCtor(schema, 'Client', WorkerClientInstance))
    throw new Error('bad client ctor');
  if (!hasKey(schema, 'SUBSCRIPTION'))
    throw new Error('missing subscription enum');
  if (!hasKey(schema, 'MESSAGE')) throw new Error('missing message label enum');
  if (
    !hasKey(schema, 'subs') ||
    schema.subs === null ||
    typeof schema.subs !== 'object'
  )
    throw new Error('missing submap');
  for (const label of Object.keys(subscriptions)) {
    if (subscriptions[label] !== label)
      throw new Error(`malformed subscriptions label ${label}`);
    if (!hasCtor(schema, label, WorkerSubInstance))
      throw new Error(`bad sub ctor for ${label}`);
  }
  for (const label of Object.keys(messageLabels)) {
    if (messageLabels[label] !== label)
      throw new Error(`malformed message label ${label}`);
  }
  return schema;
}

// src/internal-message.ts
var INTERNAL = /* @__PURE__ */ ((INTERNAL2) => {
  INTERNAL2['CONFIG_ME'] = 'CONFIG_ME';
  INTERNAL2['HELLO_CLIENT'] = 'HELLO_CLIENT';
  INTERNAL2['PING'] = 'PING';
  INTERNAL2['PONG'] = 'PONG';
  return INTERNAL2;
})(INTERNAL || {});
function decodeInternal(payload) {
  const [ns, subId, label, data] = payload;
  if (ns !== null || INTERNAL[label] !== label)
    throw new Error(`this is NOT an internal message. ${ns}:${label}`);
  if (data === void 0)
    throw new Error(`internal message with undefined data! ${ns}:${label}`);
  return {
    subId: subId ?? null,
    label,
    data,
  };
}

// src/connection.ts
function ClientConnection(id, port, receive) {
  return new ClientConnectionImpl(id, port, receive);
}
function WorkerClientConnection(port, receive) {
  return new ClientConnectionImpl(null, port, receive);
}
var ClientConnectionImpl = class {
  id;
  port;
  alive = true;
  subs;
  receive;
  constructor(id, port, receive) {
    this.id = id ?? UUID();
    this.port = port;
    this.subs = /* @__PURE__ */ new Map();
    this.receive = receive;
    this.port.addEventListener('message', ({ data, ports }) => {
      const reply = ports[0];
      this.receive(
        this,
        data,
        reply &&
          ((r) => {
            reply?.postMessage(r);
          }),
      );
    });
    this.port.addEventListener('messageerror', (e) => {
      console.error(`message error for connection#${this.id}`, e);
    });
    this.port.start();
  }
  addSub(id) {
    id ??= UUID();
    if (this.subs.has(id))
      throw new Error(`sub connection already created for "${id}"`);
    const sub = new SubConnectionImpl(id, this, true);
    this.subs.set(sub.id, sub);
    return sub;
  }
  close() {
    this.alive = false;
    for (const id of this.subs.keys()) this.removeSub(id);
  }
  [Symbol.iterator]() {
    return this.subs.values();
  }
  getSub(id) {
    return this.subs.get(id);
  }
  removeSub(id) {
    const sub = this.subs.get(id);
    sub.alive = false;
    this.subs.delete(id);
  }
  // TODO - can probably just get rid of label here, but I'll leave it for now.
  send(subId, namespace, label, handler, data) {
    if (!this.alive)
      throw new Error(`client connection ${this.id} is not alive!`);
    const payload = encodeMessage({ subId, namespace, label, data, handler });
    this.port.postMessage(payload);
  }
  request(subId, namespace, label, handler, data) {
    if (!this.alive)
      throw new Error(`client connection ${this.id} is not alive!`);
    const payload = encodeMessage({ subId, namespace, label, data, handler });
    return awaitableRequest(this.port, payload, null);
  }
  // this is only for internal gobblin messages (im hungry, found a cave, etc)
  requestInternal(label, data, timeout = null) {
    if (!this.alive)
      throw new Error(`client connection ${this.id} is not alive!`);
    const payload = encodeMessage({
      label,
      data,
      subId: null,
      namespace: null,
      handler: '',
    });
    console.log('send request internal', payload);
    return awaitableRequest(this.port, payload, timeout);
  }
};
var SubConnectionImpl = class {
  id;
  client;
  alive;
  constructor(id, client, alive = true) {
    this.id = id;
    this.client = client;
    this.alive = alive;
  }
  send(ns, label, handler, data) {
    if (this.alive) this.client.send(this.id, ns, label, handler, data);
    else throw new Error(`sub ${this.id} is already dead!`);
  }
  request(ns, label, handler, data) {
    if (this.alive)
      return this.client.request(this.id, ns, label, handler, data);
    else throw new Error(`sub ${this.id} is already dead!`);
  }
  close() {
    this.client.removeSub(this.id);
  }
};
function awaitableRequest(port, payload, timeout = null) {
  return new Promise((resolve) => {
    const channel = new MessageChannel();
    function close(reply) {
      channel.port1.close();
      channel.port2.close();
      resolve(reply);
    }
    channel.port1.addEventListener('message', (e) => {
      close(e.data);
    });
    channel.port1.addEventListener('onmessageerror', (e) => {
      console.warn('async onmessageerror', { event: e, payload });
      close(null);
    });
    if (timeout !== null)
      setTimeout(() => {
        close(null);
      }, timeout);
    port.postMessage(payload, [channel.port2]);
    channel.port1.start();
  });
}
function bootstrapClient(worker, config) {
  return new Promise((resolve, reject) => {
    function onMessage(e) {
      const { data, ports } = e;
      const sendReply = ports[0];
      try {
        const message = decodeInternal(data);
        switch (message.label) {
          case 'CONFIG_ME' /* CONFIG_ME */:
            if (!config) throw new Error('you REALLY need a config now');
            if (!sendReply)
              throw new Error('i have no MessagePort and I must config');
            sendReply.postMessage(config);
            done(message.data);
            return;
          case 'HELLO_CLIENT' /* HELLO_CLIENT */:
            sendReply.postMessage(void 0);
            done(message.data);
            return;
        }
      } catch (ex) {
        if (sendReply) sendReply.postMessage(ex);
        done(
          ex instanceof Error
            ? ex
            : new Error(`client bootstrap failed: ${ex}`),
        );
      }
    }
    function done(result) {
      worker.port.removeEventListener('message', onMessage);
      worker.port.removeEventListener('messageerror', reject);
      if (result && isUUID(result)) resolve(result);
      else reject(result);
    }
    worker.port.addEventListener('message', onMessage);
    worker.port.addEventListener('messageerror', reject);
    worker.port.start();
  });
}

// src/client.ts
var GoblinClient = class {
  id;
  state = 'STARTUP' /* STARTUP */;
  //private fatalError: Error|null;
  name;
  // like host app name, basically.
  schemas;
  services = createClientMap();
  // TODO - private but I am testing~
  connection;
  worker;
  constructor(args) {
    this.schemas = args.schemas;
    this.name = args.name;
    this.worker = args.worker;
    this.id = args.id;
    this.connection = ClientConnection(
      this.id,
      this.worker.port,
      this.onReceive.bind(this),
    );
    this.state = 'CONNECTED' /* CONNECTED */;
    if (this.schemas.length === 0) console.warn('forgot to add services?');
    for (const schema of this.schemas) this.registerService(schema);
    this.state = 'ALIVE' /* ALIVE */;
    console.log('goblin client has state:', this.state);
    this.onAlive();
  }
  registerService(schema) {
    this.services.set(schema, this.id, this.makeServiceBus(schema));
  }
  onAlive() {
    for (const record of this.services.values()) {
      record.service.onConnected?.();
    }
  }
  /**
   * methods a service can use to send message to the worker:
   *
   * @param schema
   */
  makeServiceBus(schema) {
    const bus = {};
    for (const [label, prop] of Object.entries(schema.send))
      bus[prop.bus] = (data) => {
        this.connection.send(null, schema.NAMESPACE, label, prop.handler, data);
      };
    for (const [label, prop] of Object.entries(schema.request))
      bus[prop.bus] = (data) =>
        this.connection.request(
          null,
          schema.NAMESPACE,
          label,
          prop.handler,
          data,
        );
    for (const [label, prop] of Object.entries(schema.subscribe)) {
      const subLabel = label.match(SUBSCRIBE_LABEL)?.[1];
      if (!subLabel) {
        console.error('malformed subscribe message label?', label);
        continue;
      }
      const subSchema = schema.subs[subLabel];
      const subCtor = schema[subLabel];
      bus[prop.bus] = async (data) => {
        const result = await this.connection.request(
          null,
          schema.NAMESPACE,
          label,
          prop.handler,
          data,
        );
        const { id, reply } = result;
        if (id === null) {
          console.warn(`subscription request '${label}' refused`, data, reply);
          if (reply instanceof Error) throw reply;
          else return reply;
        }
        return this.createSub(subCtor, id, data, reply, subSchema, subLabel);
      };
    }
    return bus;
  }
  createSub(subCtor, id, data, reply, subSchema, label) {
    const props = {
      id,
      clientId: this.id,
      namespace: subSchema.namespace,
      label,
    };
    const privateProps = this.createSubBus(id, subSchema);
    const sub = createInstance(subCtor, props, privateProps, data, reply);
    this.services.get(subSchema.namespace).subs.set(id, sub);
    return sub;
  }
  /**
   * methods that a sub can use to send messages to the worker.
   *
   * @param id
   * @param subSchema
   */
  createSubBus(id, subSchema) {
    const ns = subSchema.namespace;
    const sub = this.connection.addSub(id);
    const unsubLabel = `UNSUBSCRIBE_${subSchema.label}`;
    const bus = {
      unsubscribe: (data) => {
        sub.send(ns, unsubLabel, '', data);
        this.removeSub(ns, id, data, true);
      },
    };
    for (const [label, prop] of Object.entries(subSchema.send))
      bus[prop.bus] = (data) => {
        sub.send(ns, label, prop.handler, data);
      };
    for (const [label, prop] of Object.entries(subSchema.request))
      bus[prop.bus] = (data) => sub.request(ns, label, prop.handler, data);
    return bus;
  }
  onReceive(client, raw, sendReply) {
    console.assert(client === this.connection, 'wut', client, this.connection);
    if (raw[0] === null) {
      this.onReceiveInternal(raw, sendReply);
    } else {
      void this.onReceiveService(decodeMessage(raw), sendReply);
    }
  }
  onReceiveInternal(raw, sendReply) {
    const message = decodeInternal(raw);
    console.debug('internal message get', message, raw);
    switch (message.label) {
      case 'PING' /* PING */:
        return sendReply?.(message.data);
      case 'PONG' /* PONG */:
        if (sendReply) {
          sendReply(new Error('dont ask for a reply to pong'));
        }
        return;
    }
    const error = new Error(`wat should a client do with ${message.label}??`);
    if (sendReply) sendReply(error);
  }
  async onReceiveService(message, sendReply) {
    if (sendReply)
      console.warn('worker service has decided to send a request???');
    try {
      const { subId, namespace, data, handler } = message;
      const record = this.services.get(namespace);
      let reply;
      if (subId === null) record.service[handler](data);
      else if (isUUID(subId)) reply = this.onReceiveSub(subId, record, message);
      else reply = this.onReceiveSubs(subId, record, message);
      if (sendReply) sendReply(await reply);
    } catch (ex) {
      if (sendReply) sendReply(ex);
      throw ex;
    }
  }
  removeSub(namespace, id, data, fromClient) {
    const errors = [];
    try {
      this.connection.getSub(id).close();
    } catch (ex) {
      errors.push(ex);
    }
    let sub = null;
    let service = null;
    try {
      service = this.services.get(namespace);
      sub = service.subs.get(id);
    } catch (ex) {
      errors.push(ex);
    }
    try {
      if (sub) service?.subs.delete(sub.id);
    } catch (ex) {
      errors.push(ex);
    }
    try {
      sub?.onUnsubscribe?.(data, fromClient);
    } catch (ex) {
      errors.push(ex);
    }
    if (errors.length === 1) throw errors[0];
    if (errors.length > 1)
      throw new AggregateError(errors, 'sub removal failed');
  }
  onReceiveSub(id, record, message) {
    if (UNSUBSCRIBE_LABEL.test(message.label)) {
      this.removeSub(message.namespace, id, message.data, false);
      return;
    }
    this.connection.getSub(id);
    const sub = record.subs.get(id);
    sub[message.handler](message.data);
  }
  onReceiveSubs(ids, record, message) {
    return Promise.all(
      ids.map(async (id) => {
        try {
          return this.onReceiveSub(id, record, message);
        } catch (ex) {
          return ex instanceof Error ? ex : new Error(String(ex));
        }
      }),
    );
  }
};
async function connect(args) {
  if (window.__GOBLIN_CLIENT) {
    console.log('goblin client already started, hope that works for u');
    return true;
  }
  const name = `[thread goblin] ${args.name}`;
  if (!args.config) throw new Error('currently you always need a config.');
  const worker = new SharedWorker(args.workerURL, { name, type: 'module' });
  const id = await bootstrapClient(worker, args.config);
  if (window.__GOBLIN_CLIENT)
    throw new Error('some freak beat us to the punch');
  const schemas = args.schemas.map((s) => {
    try {
      return validateClientSchema(s.NAMESPACE, s.SUBSCRIPTION, s.MESSAGE, s);
    } catch (ex) {
      const error = ex instanceof Error ? ex : new Error(String(ex));
      console.error(`schema validation failed:`, error.message, s);
      throw error;
    }
  });
  window.__GOBLIN_CLIENT = new GoblinClient({ id, worker, name, schemas });
  return true;
}

// src/error.ts
var BadMessageError = class extends Error {
  name = 'BadMessageError';
  constructor(message, admonition = 'Bad Message') {
    const text = `${message.namespace}.${message.label}: ${admonition}`;
    console.debug(text, message);
    super(text);
  }
};

// ../../node_modules/.pnpm/safari-14-idb-fix@3.0.0/node_modules/safari-14-idb-fix/dist/index.js
function idbReady() {
  var isSafari =
    !navigator.userAgentData &&
    /Safari\//.test(navigator.userAgent) &&
    !/Chrom(e|ium)\//.test(navigator.userAgent);
  if (!isSafari || !indexedDB.databases) return Promise.resolve();
  var intervalId;
  return new Promise(function (resolve) {
    var tryIdb = function () {
      return indexedDB.databases().finally(resolve);
    };
    intervalId = setInterval(tryIdb, 100);
    tryIdb();
  }).finally(function () {
    return clearInterval(intervalId);
  });
}
var dist_default = idbReady;

// ../../node_modules/.pnpm/idb-keyval@6.1.0/node_modules/idb-keyval/dist/index.js
function promisifyRequest(request) {
  return new Promise((resolve, reject) => {
    request.oncomplete = request.onsuccess = () => resolve(request.result);
    request.onabort = request.onerror = () => reject(request.error);
  });
}
function createStore(dbName, storeName) {
  const dbp = dist_default().then(() => {
    const request = indexedDB.open(dbName);
    request.onupgradeneeded = () => request.result.createObjectStore(storeName);
    return promisifyRequest(request);
  });
  return (txMode, callback) =>
    dbp.then((db) =>
      callback(db.transaction(storeName, txMode).objectStore(storeName)),
    );
}
var defaultGetStoreFunc;
function defaultGetStore() {
  if (!defaultGetStoreFunc) {
    defaultGetStoreFunc = createStore('keyval-store', 'keyval');
  }
  return defaultGetStoreFunc;
}
function get(key, customStore = defaultGetStore()) {
  return customStore('readonly', (store) => promisifyRequest(store.get(key)));
}
function set(key, value, customStore = defaultGetStore()) {
  return customStore('readwrite', (store) => {
    store.put(value, key);
    return promisifyRequest(store.transaction);
  });
}
function del(key, customStore = defaultGetStore()) {
  return customStore('readwrite', (store) => {
    store.delete(key);
    return promisifyRequest(store.transaction);
  });
}

// ../auth/src/auth-controller.ts
var IDB_AUTH_DATA_KEY = 'authData';
var AuthController = class {
  authState = {
    data: false,
    fetching: true,
    error: null,
    failed: false,
  };
  accessToken = null;
  subscribers = [];
  abortController = null;
  fetchingPromise = null;
  config = null;
  constructor() {
    void this.getAuthDataFromIDB();
  }
  setConfig(config) {
    this.config = config;
  }
  notifySubscribers() {
    this.subscribers.forEach((callback) => {
      callback(this.getAuthState());
    });
  }
  // method should be reworked after refresh token introduced
  async fetchAuthState() {
    if (!this.config) throw new Error('AuthController is not configured');
    this.abortController = new AbortController();
    try {
      const authData = await this.config.fetchAuthData({
        abortController: this.abortController,
      });
      this.accessToken = authData.accessToken;
      this.authState = {
        ...this.authState,
        fetching: false,
        data: true,
        error: null,
        failed: false,
      };
    } catch (error) {
      const typedError =
        error instanceof Error
          ? error
          : new Error(error?.toString() ?? 'Unknown error');
      this.authState = {
        ...this.authState,
        fetching: false,
        error: typedError,
        failed: true,
      };
      throw error;
    }
  }
  subscribeToAuthState(callback) {
    this.subscribers.push(callback);
    return () => {
      this.subscribers = this.subscribers.filter((sub) => sub !== callback);
    };
  }
  async updateAuthState() {
    if (this.fetchingPromise) return this.fetchingPromise;
    this.authState.fetching = true;
    this.notifySubscribers();
    this.fetchingPromise = this.fetchAuthState().catch((error) => {
      this.authState.fetching = false;
      this.notifySubscribers();
      throw error;
    });
    await this.fetchingPromise;
    this.fetchingPromise = null;
    this.authState.fetching = false;
    this.notifySubscribers();
  }
  signOut() {
    this.abortController?.abort();
    this.fetchingPromise = null;
    void del(IDB_AUTH_DATA_KEY);
    this.authState = {
      ...this.authState,
      data: false,
      fetching: false,
      error: null,
      failed: false,
    };
    this.accessToken = null;
    this.notifySubscribers();
  }
  getToken() {
    if (!this.accessToken) {
      if (this.authState.fetching) {
        throw new Error('Too early to get token, fetching in progress');
      }
      throw new Error('Access token is not set. Not Authorized');
    }
    return this.accessToken;
  }
  getAuthState() {
    return {
      ...this.authState,
    };
  }
  /**
   * Method to set dev auth data
   * @param authData
   */
  setAuthData(authData) {
    if (authData instanceof Error) {
      this.authState = {
        ...this.authState,
        fetching: false,
        data: false,
        error: authData,
        failed: true,
      };
    } else {
      void this.saveAuthDataToIDB(authData);
      this.accessToken = `Bearer ${authData.accessToken}`;
      this.authState = {
        ...this.authState,
        fetching: false,
        data: true,
        error: null,
        failed: false,
      };
    }
    this.notifySubscribers();
  }
  async saveAuthDataToIDB(authData) {
    try {
      return set(IDB_AUTH_DATA_KEY, authData);
    } catch (error) {
      throw new Error(`Error from idb while storing auth data: ${error}`);
    }
  }
  async getAuthDataFromIDB() {
    try {
      const authData = await get(IDB_AUTH_DATA_KEY);
      if (authData) {
        this.authState = {
          ...this.authState,
          fetching: false,
          data: true,
        };
        this.accessToken = `Bearer ${authData.accessToken}`;
      } else {
        this.authState = {
          ...this.authState,
          fetching: false,
          data: false,
        };
      }
      this.notifySubscribers();
      return authData ?? null;
    } catch (error) {
      throw new Error(`Error from idb while getting auth data: ${error}`);
    }
  }
};

// src/worker.ts
var GoblinWorker = class {
  state = 'UNDER_CONSTRUCTION' /* UNDER_CONSTRUCTION */;
  fatalError = null;
  name;
  // like host app name, basically.
  schemas;
  services = createWorkerMap();
  clients = createHardMap();
  configuring = null;
  config = null;
  constructor(args) {
    try {
      this.schemas = args.schemas;
      this.name = this.validateWorker(args.name);
      self.addEventListener('connect', this.onConnectEvent.bind(this));
      self.addEventListener('error', this.onErrorEvent.bind(this));
      self.addEventListener(
        'languagechange',
        this.onLanguagechangeEvent.bind(this),
      );
      self.addEventListener('offline', this.onOfflineEvent.bind(this));
      self.addEventListener('online', this.onOnlineEvent.bind(this));
      this.state = 'STARTUP' /* STARTUP */;
      if (this.schemas.length === 0) console.warn('forgot to add services?');
      const authController = new AuthController();
      for (const schema of this.schemas)
        this.registerService(schema, authController);
      this.state = 'CONFIGLESS' /* CONFIGLESS */;
    } catch (ex) {
      this.name = `[failed goblin] ${args.name}`;
      this.megaDie(ex);
      throw new Error('on top of everything megadie is broken too');
    }
  }
  /**
   * reserved for Truly unrecoverable error states: die instantly but also
   * continue to complain about it until the worker literally stops running.
   * take a bit of care not to pollute error logs with non-useful stack
   * traces unless absolutely necesssary.
   *
   * @param ex
   */
  megaDie(ex) {
    this.state = 'SQUISHED' /* SQUISHED */;
    const error = ex instanceof Error ? ex : null;
    const message = error ?? String(ex);
    if (this.fatalError) {
      console.warn('got yet another error:', message);
    } else {
      this.fatalError = error ?? new Error(String(ex));
      throw this.fatalError;
    }
  }
  /**
   * make sure we are in a SharedWorker and that it has the right name.
   *
   * @param name
   */
  validateWorker(name) {
    if (this.state !== 'UNDER_CONSTRUCTION' /* UNDER_CONSTRUCTION */)
      throw new Error('its too late for that now!');
    const workerName = `[thread goblin] ${name}`;
    if (!weAreInASharedWorker())
      throw new Error('This is no place for a goblin worker...');
    if (self.name !== workerName)
      throw new Error(
        `GoblinWorker name is "${workerName}" but SharedWorker label is "${self.name}". concerning...`,
      );
    return workerName;
  }
  ////// (Shared)WorkerScope event handlers /////
  /**
   * 'connect' MessageEvent
   *
   * @param e
   */
  async onConnectEvent(e) {
    console.debug('we got a connection', e);
    switch (this.state) {
      case 'UNDER_CONSTRUCTION' /* UNDER_CONSTRUCTION */:
      case 'STARTUP' /* STARTUP */:
        this.megaDie(
          new Error(`we're still starting, did you make an async constructor?`),
        );
        break;
      case 'CONFIGLESS' /* CONFIGLESS */:
        return this.onHostConnected(e);
      case 'BEING_CONFIGURED' /* BEING_CONFIGURED */:
        if (this.configuring) void (await this.configuring);
        else console.warn('uh oh, state got out of wack?');
      case 'ALIVE' /* ALIVE */: {
        this.onClientConnected(e);
        return;
      }
      case 'SQUISHED' /* SQUISHED */:
        throw new Error('already dead, ignoring new connection.');
    }
  }
  /**
   * @param {Event} e — im not sure we can actually do anything useful with this.
   */
  onErrorEvent(e) {
    console.debug('we got an "error" event', e);
  }
  /**
   * @param {Event} e — 'languagechange', the user set a new language preference
   *   in their browser. hope it's better than typescript LOL
   */
  onLanguagechangeEvent(e) {
    console.debug('we got a "languagechange" event', e);
  }
  /**
   * @param {Event} e — 'offline' internet went away
   */
  onOfflineEvent(e) {
    console.debug('we got an "offline" event', e);
  }
  /**
   * @param {Event} e — 'online' internet came back
   */
  onOnlineEvent(e) {
    console.debug('we got an "online" event', e);
  }
  ///// Service Management /////
  /**
   * adding a new service. we provide it with the methods needed to communicate
   * with its counterpart on client threads.
   *
   * @param {GoblinServiceWorkerSchema} schema provided at startup. Maybe we can
   *   supporting loading new services later on?
   */
  registerService(schema, authController) {
    if (this.state !== 'STARTUP' /* STARTUP */) {
      console.error('no adding new services after startup (yet!)');
      return;
    }
    const bus = this.makeWorkerBus(schema);
    this.services.set(schema, bus, authController);
  }
  /**
   * after getting a config from the client, go through all the steps to
   * finalize it. this system is totally overwrought, not sure why I thought it
   * was important to have a config that was shared by all the services.
   * I think the only change i would make for it to be sound is just not try to
   * store the same config for everyone.
   *
   * @param rawConfig
   */
  useRawConfig(rawConfig) {
    try {
      if (!rawConfig) throw new Error('client didnt send us a config');
      const proposedConfig = this.buildConfig(rawConfig);
      const validatedConfig = this.validateConfig(proposedConfig);
      this.setConfig(validatedConfig);
      return true;
    } catch (ex) {
      console.error('config has been refused:', ex);
      return false;
    }
  }
  /**
   * each service in turn has the chance to "inflate" the raw config with
   * their needed information. TODO - don't try to accumulate config changes.
   *
   * @param rawConfig
   */
  buildConfig(rawConfig) {
    for (const { service } of this.services.values()) {
      const builtConfig = service.buildConfig(rawConfig, this.config);
      if (builtConfig !== rawConfig)
        throw new Error('someone tried to pull a fast one on our config!!!');
    }
    return rawConfig;
  }
  validateConfig(proposedConfig) {
    for (const { service } of this.services.values()) {
      if (!service.validateConfig(proposedConfig))
        throw new Error(
          `${service.namespace} said this config just will not do`,
        );
    }
    return proposedConfig;
  }
  setConfig(newConfig) {
    for (const { service } of this.services.values())
      service.setConfig(newConfig, this.config);
  }
  ////// Client Connection Management //////
  /**
   * make sure client is paying attention. this is the only way we have to do
   * this, lol.
   *
   * @param client
   */
  async pingClient(client) {
    try {
      const ping = Math.random();
      const pong = await client.requestInternal(
        'PING' /* PING */,
        ping,
        PING_TIMEOUT_MS,
      );
      if (pong === null) {
        console.warn('ping-pong timeout');
        return;
      }
      if (ping !== pong) {
        console.error('WHAT THE HELL', ping, pong);
      }
    } catch {
      console.error('client stopped responding to pings!', client);
      this.removeClient(client);
      return;
    }
    setTimeout(this.pingClient.bind(this, client), PING_PONG_INTERVAL_MS);
  }
  /**
   * when the first client connects (it better be the one started us up) add
   * it to the list but also berate it for a configuration.
   *
   * @param e
   */
  async onHostConnected(e) {
    const client = this.createConnection(e);
    this.state = 'BEING_CONFIGURED' /* BEING_CONFIGURED */;
    this.configuring = client.requestInternal(
      'CONFIG_ME' /* CONFIG_ME */,
      client.id,
    );
    const rawConfig = await this.configuring;
    if (this.state !== 'BEING_CONFIGURED' /* BEING_CONFIGURED */) {
      console.warn('someone beat us to the punch? must be another tab...');
      return;
    }
    if (!this.useRawConfig(rawConfig)) {
      this.megaDie('we did not get a usable config');
    }
    this.state = 'ALIVE' /* ALIVE */;
  }
  onClientConnected(e) {
    if (this.state === 'SQUISHED' /* SQUISHED */)
      throw new Error('impossible to connect during squished state.');
    const client = this.createConnection(e);
    void client.requestInternal('HELLO_CLIENT' /* HELLO_CLIENT */, client.id);
  }
  ///// Incoming Message Handlers /////
  onReceive(client, raw, sendReply) {
    if (raw[0] === null) {
      this.onReceiveInternal(client, raw, sendReply);
    } else {
      void this.onReceiveService(client, decodeMessage(raw), sendReply);
    }
  }
  onReceiveInternal(client, raw, sendReply) {
    const message = decodeInternal(raw);
    switch (message.label) {
      case 'PING' /* PING */: {
        this.onClientPing(client, message, sendReply);
        return;
      }
      case 'PONG' /* PONG */: {
        this.onClientPong(client, message, sendReply);
        return;
      }
    }
    const error = new Error(
      `um. thank u for the internal message but we dont want any ${message.label} from clients.`,
    );
    if (sendReply) sendReply(error);
    throw error;
  }
  onClientPong(client, _raw, sendReply) {
    console.error(`client ${client.id} ponged with no ping???`);
    if (sendReply) sendReply(new Error('wtf'));
  }
  onClientPing(client, raw, sendReply) {
    if (sendReply) sendReply(raw.data);
    else
      console.error(
        `${client.id} THIS MOTHERFUCKER IS NOT SERIOUS ABOUT PING/PONG`,
      );
  }
  async onReceiveService(connection, message, sendReply) {
    const record = this.services.get(message.namespace);
    const { subId, data, label, handler } = message;
    const client = record.clients.has(connection.id)
      ? record.clients.get(connection.id)
      : this.createWorkerClient(record, connection);
    if (UNSUBSCRIBE_LABEL.test(label)) {
      this.unsubscribeFromClientSide(connection, message, sendReply);
      return;
    }
    if (SUBSCRIBE_LABEL.test(label))
      return this.createSubscription(
        connection,
        client,
        record,
        message,
        // ill show you a fucking void expression
        // eslint-disable-next-line @typescript-eslint/no-confusing-void-expression
        client[handler](data),
        sendReply,
      );
    try {
      let reply;
      if (subId === null) reply = client[handler](data);
      else if (isUUID(subId))
        reply = this.onReceiveSub(subId, record, connection, message);
      else reply = this.onReceiveSubs(subId, record, connection, message);
      if (sendReply) sendReply(await reply);
    } catch (ex) {
      if (sendReply) sendReply(ex);
      throw ex;
    }
  }
  onReceiveSub(id, record, connection, message) {
    connection.getSub(id);
    const sub = record.subs.get(id);
    void sub[message.handler](message.data);
  }
  onReceiveSubs(ids, record, connection, message) {
    return Promise.all(
      ids.map(async (id) => {
        try {
          return this.onReceiveSub(id, record, connection, message);
        } catch (ex) {
          return ex instanceof Error ? ex : new Error(String(ex));
        }
      }),
    );
  }
  ///// Instance Manangement /////
  // GoblinWorker ⇄ GoblinClient
  createWorkerClient(record, connection) {
    const props = {
      id: connection.id,
      namespace: record.namespace,
    };
    const privateProps = this.makeWorkerClientBus(record.schema, connection);
    const client = createInstance(record.schema.Client, props, privateProps);
    record.clients.set(client.id, client);
    record.service.onClientConnected?.(client);
    void this.pingClient(connection);
    return client;
  }
  // GoblinWorkerService ⇄ GoblinClientService (the actual MessagePort part)
  createConnection(e) {
    const port = e.ports[0];
    const client = WorkerClientConnection(port, this.onReceive.bind(this));
    this.clients.set(client.id, client);
    return client;
  }
  // carefully close everything
  removeClient(connection) {
    const id = connection.id;
    console.debug(`removing connection[${id}] connection`);
    const errors = [];
    try {
      connection.close();
    } catch (ex) {
      const m = `client[${id}].close failed`;
      console.warn(m, ex);
      errors.push(ex instanceof Error ? ex : new Error(m));
    }
    for (const record of this.services.values()) {
      const ns = record.namespace;
      if (record.clients.has(id)) {
        const client = record.clients.get(id);
        try {
          client.onDisconnected?.();
        } catch (ex) {
          const m = `${ns}:client[${id}].onDisconnected failed:`;
          console.warn(m);
          errors.push(ex instanceof Error ? ex : new Error(m));
        }
        try {
          record.service.onClientDisconnected?.(client);
        } catch (ex) {
          const m = `${ns}.onClientDisconnected(${id}) failed: ${ex}`;
          console.warn(m);
          errors.push(ex instanceof Error ? ex : new Error(m));
        }
      }
    }
    if (errors.length === 1) throw errors[0];
    if (errors.length > 1)
      throw new AggregateError(errors, 'removeClient had problems');
  }
  /**
   * GoblinWorkerSub ⇄ GoblinClientSub
   *
   * @param connection
   * @param client
   * @param service
   * @param message
   * @param reply
   * @param sendReply
   */
  async createSubscription(
    connection,
    client,
    service,
    message,
    reply,
    sendReply,
  ) {
    try {
      if (!sendReply) throw new BadMessageError(message, `no reply port`);
      reply = await reply;
      if (reply === null || reply instanceof Error) {
        sendReply({ id: null, reply });
        return;
      }
      const subConnection = connection.addSub();
      const id = subConnection.id;
      const subLabel = message.label.match(SUBSCRIBE_LABEL)?.[1];
      if (!subLabel) throw new BadMessageError(message, 'no sub label??');
      const subSchema = service.schema.subs[subLabel];
      const subCtor = service.schema[subLabel];
      const props = {
        id,
        clientId: client.id,
        subLabel,
        namespace: service.namespace,
      };
      const privateProps = this.makeSubBus(subSchema, subConnection);
      const sub = createInstance(
        subCtor,
        props,
        privateProps,
        message.data,
        reply,
      );
      service.subs.set(id, sub);
      client.onSubscribed?.(sub);
      sendReply({ id, reply });
    } catch (ex) {
      if (sendReply) sendReply({ id: null, reply: ex });
      throw ex;
    }
  }
  removeSubscription(id, namespace, data, fromClient) {
    let record = null;
    let sub = null;
    let error = null;
    try {
      record = this.services.get(namespace);
      sub = record.subs.get(id);
    } catch (ex) {
      console.error('removeSub failed', { id, namespace, data, record }, ex);
      error = ex instanceof Error ? ex : new Error(String(ex));
    }
    if (sub) sub.onUnsubscribe?.(data, fromClient);
    if (record) record.subs.delete(id);
    if (error !== null) throw error;
  }
  unsubscribeWorkerSide(subConnection, namespace, label, data) {
    const errors = [];
    try {
      subConnection.send(namespace, `UNSUBSCRIBE_${label}`, '', data);
      subConnection.close();
    } catch (ex) {
      console.error(
        'worker unsubbed from already-closed sub?',
        {
          subConnection,
          namespace,
          label,
          data,
        },
        ex,
      );
      errors.push(ex);
    }
    try {
      this.removeSubscription(subConnection.id, namespace, data, false);
    } catch (ex) {
      errors.push(ex);
    }
    if (errors.length === 1) throw errors[0];
    if (errors.length > 1) throw new AggregateError(errors, 'unsub failed');
  }
  unsubscribeFromClientSide(connection, message, sendReply) {
    const errors = [];
    if (sendReply) {
      const wtf = new Error('unsubscribe messages are not requests!');
      sendReply(wtf);
      errors.push(wtf);
    }
    let { subId, data, namespace } = message;
    if (subId === null) throw new Error('no sub ids given!');
    if (isUUID(subId)) subId = [subId];
    for (const id of subId) {
      try {
        connection.removeSub(id);
      } catch (ex) {
        errors.push(ex);
      }
      try {
        this.removeSubscription(id, namespace, data, true);
      } catch (ex) {
        errors.push(ex);
      }
    }
    if (errors.length === 1) {
      console.error('failed to unsubscribe', message, errors[0]);
      throw errors[0];
    } else if (errors.length > 1) {
      console.error('lots of problems unsubscribing', message, errors);
      throw new AggregateError(errors, 'lots of problems unsubscribing');
    }
  }
  ///// Bus Creation /////
  /**
   * send a message to all clients/subs at once, bound by make*Bus methods.
   *
   * @param namespace
   * @param label
   * @param data
   */
  sendBroadcastMessage(namespace, label, handler, data) {
    for (const client of this.clients.values())
      client.send(null, namespace, label, handler, data);
  }
  /**
   * all the methods a service needs to blast broadcast messages at this client:
   *
   * @param schema
   */
  makeWorkerBus(schema) {
    const entries = Object.entries({
      ...schema.broadcast,
      ...schema.broadcastSubs,
    });
    const bus = {};
    for (const [label, prop] of entries) {
      bus[prop.bus] = this.sendBroadcastMessage.bind(
        this,
        schema.NAMESPACE,
        label,
        prop.handler,
      );
    }
    return bus;
  }
  makeWorkerClientBus(schema, connection) {
    const bus = {};
    for (const [label, prop] of Object.entries(schema.direct)) {
      bus[prop.bus] = connection.send.bind(
        connection,
        null,
        schema.NAMESPACE,
        label,
        prop.handler,
      );
    }
    return bus;
  }
  /**
   * all the methods a service needs to send messages to this subscription:
   *
   * @param subSchema
   * @param connection
   */
  makeSubBus(subSchema, connection) {
    const bus = {
      unsubscribe: this.unsubscribeWorkerSide.bind(
        this,
        connection,
        subSchema.namespace,
        subSchema.label,
      ),
    };
    for (const [label, prop] of Object.entries(subSchema.direct)) {
      bus[prop.bus] = connection.send.bind(
        connection,
        subSchema.namespace,
        label,
        prop.handler,
      );
    }
    return bus;
  }
};
function weAreInASharedWorker() {
  return (
    'SharedWorkerGlobalScope' in globalThis && // @ts-expect-error - out of patience
    typeof globalThis.SharedWorkerGlobalScope === 'function' &&
    self instanceof self.SharedWorkerGlobalScope
  );
}
function work(args) {
  self.__GOBLIN_WORKER = new GoblinWorker({
    name: args.name,
    schemas: args.schemas.map((s) => {
      try {
        return validateWorkerSchema(s.NAMESPACE, s.SUBSCRIPTION, s.MESSAGE, s);
      } catch (ex) {
        const error = ex instanceof Error ? ex : new Error(String(ex));
        console.error(`schema validation failed:`, error.message, s);
        throw error;
      }
    }),
  });
}

// src/namespace.ts
var NAMESPACE_CHARS = /^[A-Z]+(?:_[A-Z]+)*$/;
function validateNamespace(namespace, existing, assert) {
  return validate(
    'Service Namespace',
    'validateNamespace',
    namespace,
    namespace,
    existing,
    assert,
  );
}
function validateLabel(namespace, label, existing, assert) {
  return validate('Label', 'validateLabel', namespace, label, existing, assert);
}
function validate(typeName, functionName, namespace, t, existing, assert) {
  const [exists, onInvalid, onValid] = parseArgs(
    t,
    existing,
    assert,
    functionName,
  );
  if (typeof t !== 'string') {
    try {
      void `${namespace} and ${t} can convert to primitive values e.g. String`;
    } catch (ex) {
      return onInvalid(
        '???',
        `${typeName}s must be strings, (this can't even be converted to a value type!!!)`,
        String(ex?.message ?? ex),
      );
    }
    return onInvalid(namespace, `${typeName}s must be strings.`, String(t));
  } else if (!NAMESPACE_CHARS.test(t)) {
    return onInvalid(
      namespace,
      `${typeName}s must use CONST_CASE and cannot start/end with underscore.`,
      t,
    );
  } else if (exists) {
    return onInvalid(namespace, `this ${typeName} is already in use.`, t);
  } else {
    return onValid(t);
  }
}
function parseArgs(t, existing, assert, functionName) {
  if (assert === void 0) {
    if (existing === void 0 || existing === false)
      return [false, falseOnInvalid, trueOnValid];
    else if (existing === true) return [false, throwOnInvalid, resultOnValid];
    else return [existing.has(t), falseOnInvalid, trueOnValid];
  }
  if (
    // validate(..., undefined, boolean) => WRONG!
    existing === void 0 || // validate(..., boolean, boolean) => WRONG!
    typeof existing === 'boolean'
  ) {
    throw new Error(`
      as much as we can all appreciate the spirit of thwarting typescript,
      you have called ${functionName} in an invalid manner :-/
    `);
  }
  return [
    existing.has(t),
    assert ? throwOnInvalid : falseOnInvalid,
    assert ? resultOnValid : trueOnValid,
  ];
}
function falseOnInvalid() {
  return false;
}
function trueOnValid() {
  return true;
}
function throwOnInvalid(namespace, message, label) {
  throw new ServiceDefinitionError(namespace, message, label);
}
function resultOnValid(l) {
  return l;
}
var KEBAB_NO_USCORE = /_/g;
var KEBAB_CHARS = /[^\da-z-]+/g;
function constToKebab(s) {
  return s.replace(KEBAB_NO_USCORE, '-').toLowerCase().replace(KEBAB_CHARS, '');
}
var FILE_NO_DDASH = /-+/g;
function kebabFilename(...parts) {
  return parts.map(constToKebab).join('-').replace(FILE_NO_DDASH, '-');
}
var Y_2_T = /_?([A-Z])([A-Z]*)/g;
function constToType(label, suffix = '') {
  return label.replace(Y_2_T, (_, f, rest) => f + rest.toLowerCase()) + suffix;
}

// src/uuid.ts
var SIDE = 7;
var MOD_HK = Math.pow(10, SIDE);
var LENGTH = SIDE * 2 + 1;
var ZERO = 48;
var DASH = 45;
var NINE = 57;
var code;
function isUUID(s) {
  if (
    typeof s !== 'string' ||
    s.length !== LENGTH ||
    s.charCodeAt(SIDE) !== DASH
  )
    return false;
  for (let i = 0; i < SIDE; i++) {
    if (
      (code = s.charCodeAt(i)) < ZERO ||
      code > NINE ||
      (code = s.charCodeAt(i + 1 + SIDE)) < ZERO ||
      code > NINE
    )
      return false;
  }
  return true;
}
var __GOBLIN_NEXT_UUID_INTERNAL = Object.hasOwn(
  globalThis,
  '__GOBLIN_NEXT_UUID',
)
  ? Number(globalThis.__GOBLIN_NEXT_UUID ?? 0)
  : 0;
Object.defineProperty(globalThis, '__GOBLIN_NEXT_UUID', {
  get: () => String(++__GOBLIN_NEXT_UUID_INTERNAL % MOD_HK).padStart(7, '0'),
  configurable: true,
  // for hmr and stuff
});
function toUUID(s) {
  if (!isUUID(s)) throw new Error('where on earth did you find this string???');
  return s;
}
function UUID() {
  const monoTony = globalThis.__GOBLIN_NEXT_UUID;
  const randy = Math.random().toFixed(7).slice(2);
  return toUUID(`${monoTony}-${randy}`);
}
export {
  BadMessageError,
  ClientConnection,
  ClientServiceInstance,
  ClientSubInstance,
  GoblinClient,
  GoblinWorker,
  MESSAGE,
  ServiceDefinitionError,
  UUID,
  WorkerClientConnection,
  WorkerClientInstance,
  WorkerServiceInstance,
  WorkerSubInstance,
  bootstrapClient,
  connect,
  constToKebab,
  constToType,
  decodeMessage,
  encodeMessage,
  isUUID,
  kebabFilename,
  toUUID,
  validateLabel,
  validateNamespace,
  work,
};
